import React, { useCallback, useEffect, useState } from 'react'
import {
  Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Select, Tab, Tabs, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ClientService from '../../services/ClientService'
import ClientDashboardNotificationService from '../../services/ClientDashboardNotificationService'
import GlobalTip from '../../providers/GlobalTip'
import SectionTitle from '../../components/common/SectionTitle'

const TYPES = [
  ['advisor', 'M1 Advisor'],
  ['journey', 'M1 Journey'],
  ['event', 'M1 Events'],
]
const EMPTY = { client_id: '', title: '', message: '', event_date: '', status: 'draft', release_at: '', expire_at: '', sort_order: 0 }
const clientLabel = (client) => [client?.ClientCode ? `[${client.ClientCode}]` : '', client?.Name, client?.Id ? `(#${client.Id})` : ''].filter(Boolean).join(' ')
const localDateTime = (value) => value ? String(value).slice(0, 16) : ''

function NotificationDialog({ open, initial, type, clients, onClose, onSaved }) {
  const tip = GlobalTip.useMessage()
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const editing = Boolean(initial?.id)

  useEffect(() => {
    setForm(initial ? {
      client_id: initial.client_id ?? '', title: initial.title || '', message: initial.message || '',
      event_date: initial.event_date ? String(initial.event_date).slice(0, 10) : '',
      status: initial.status || 'draft', release_at: localDateTime(initial.release_at),
      expire_at: localDateTime(initial.expire_at), sort_order: initial.sort_order ?? 0,
    } : EMPTY)
  }, [initial, open])

  const save = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      tip.show('Title and message are required', 'error')
      return
    }
    if (type === 'event' && !form.event_date) {
      tip.show('Event notifications require an event date', 'error')
      return
    }
    if (form.status === 'active' && (!form.release_at || !form.expire_at)) {
      tip.show('Active notifications require release and expiry dates', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = { ...form, notification_type: type, client_id: form.client_id === '' ? null : Number(form.client_id), sort_order: Number(form.sort_order), event_date: type === 'event' ? (form.event_date || null) : null, release_at: form.release_at || null, expire_at: form.expire_at || null }
      if (editing) await ClientDashboardNotificationService.update(initial.id, payload)
      else await ClientDashboardNotificationService.create(payload)
      tip.show(editing ? 'Notification updated' : 'Notification created', 'success')
      onSaved()
    } catch (error) {
      tip.show(error?.response?.data?.message || 'Unable to save notification', 'error')
    } finally { setSaving(false) }
  }

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>{editing ? 'Edit' : 'New'} {TYPES.find(([key]) => key === type)?.[1]} notification</DialogTitle>
    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '18px !important' }}>
      <Autocomplete options={clients} getOptionLabel={clientLabel} value={clients.find((client) => Number(client.Id) === Number(form.client_id)) || null} onChange={(_, client) => setForm((current) => ({ ...current, client_id: client?.Id ?? '' }))} renderInput={(params) => <TextField {...params} label="Audience" placeholder="All Clients" helperText="Leave blank for All Clients" />} />
      <TextField label="Title" value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} required fullWidth />
      <TextField label="Message" value={form.message} onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))} required fullWidth multiline minRows={4} inputProps={{ maxLength: 5000 }} />
      {type === 'event' && <TextField type="date" label="Event date" value={form.event_date} onChange={(event) => setForm((current) => ({ ...current, event_date: event.target.value }))} InputLabelProps={{ shrink: true }} required />}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
        <Select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))} size="small" aria-label="Notification status">
          <MenuItem value="draft">Draft</MenuItem><MenuItem value="inactive">Inactive</MenuItem><MenuItem value="active">Active</MenuItem>
        </Select>
        <TextField type="number" label="Sort order" value={form.sort_order} onChange={(event) => setForm((current) => ({ ...current, sort_order: event.target.value }))} inputProps={{ min: 0 }} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
        <TextField type="datetime-local" label="Release date" value={form.release_at} onChange={(event) => setForm((current) => ({ ...current, release_at: event.target.value }))} InputLabelProps={{ shrink: true }} required={form.status === 'active'} />
        <TextField type="datetime-local" label="Expiry date" value={form.expire_at} onChange={(event) => setForm((current) => ({ ...current, expire_at: event.target.value }))} InputLabelProps={{ shrink: true }} required={form.status === 'active'} />
      </Box>
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Cancel</Button><Button onClick={save} variant="contained" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button></DialogActions>
  </Dialog>
}

export default function ClientNotificationsManagement() {
  const tip = GlobalTip.useMessage()
  const [tab, setTab] = useState('advisor')
  const [items, setItems] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogItem, setDialogItem] = useState(undefined)

  const load = useCallback(async () => {
    setLoading(true)
    try { setItems(await ClientDashboardNotificationService.list({ notification_type: tab })) }
    catch { tip.show('Unable to load client notifications', 'error') }
    finally { setLoading(false) }
  }, [tab, tip])
  useEffect(() => { load() }, [load])
  useEffect(() => { ClientService.listClientsSimple().then((result) => setClients(Array.isArray(result) ? result : [])).catch(() => setClients([])) }, [])
  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return
    try { await ClientDashboardNotificationService.remove(item.id); tip.show('Notification deleted', 'success'); load() }
    catch { tip.show('Unable to delete notification', 'error') }
  }

  return <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
    <SectionTitle title="Client Notifications" subtitle="Schedule M1 Advisor, Journey, and Events content for all clients or one selected client." />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="Client notification categories">
        {TYPES.map(([value, label]) => <Tab key={value} value={value} label={label} />)}
      </Tabs>
      <Button startIcon={<AddIcon />} variant="contained" onClick={() => setDialogItem(null)}>New notification</Button>
    </Box>
    {loading ? <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box> : <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }}>
      {items.length === 0 && <Typography color="text.secondary">No {TYPES.find(([value]) => value === tab)?.[1]} notifications have been created.</Typography>}
      {items.map((item) => <Box key={item.id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, bgcolor: '#fff', display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}><Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}><Typography fontWeight={700}>{item.title}</Typography><Chip size="small" label={item.status} color={item.status === 'active' ? 'success' : item.status === 'draft' ? 'warning' : 'default'} /><Chip size="small" variant="outlined" label={item.client_id ? clientLabel(clients.find((client) => Number(client.Id) === Number(item.client_id))) || `Client #${item.client_id}` : 'All Clients'} /></Box><Typography sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }} variant="body2">{item.message}</Typography><Typography sx={{ mt: 1 }} variant="caption" color="text.secondary">{item.notification_type === 'event' ? `Event date: ${item.event_date ? new Date(item.event_date).toLocaleDateString() : '—'} · ` : ''}Release: {item.release_at ? new Date(item.release_at).toLocaleString() : '—'} · Expiry: {item.expire_at ? new Date(item.expire_at).toLocaleString() : '—'}</Typography></Box>
        <IconButton aria-label={`Edit ${item.title}`} onClick={() => setDialogItem(item)}><EditIcon /></IconButton><IconButton aria-label={`Delete ${item.title}`} color="error" onClick={() => remove(item)}><DeleteIcon /></IconButton>
      </Box>)}
    </Box>}
    <NotificationDialog open={dialogItem !== undefined} initial={dialogItem} type={tab} clients={clients} onClose={() => setDialogItem(undefined)} onSaved={() => { setDialogItem(undefined); load() }} />
  </Box>
}

